import { ucFirst } from 'change-case'
import * as Sentry from '@sentry/browser'
import { camelToTitle } from '../utils'

/**
 * getErrorString
 *
 * parse AxiosError from the Lister API and return a human readable string.
 * If the error format is unhandled, use errorLoggerFn to report that to the team.
 *
 * Usage:
 * getErrorString(new Error('Oh no'), Sentry.captureException)
 *
 * @param err An Error object, hopefully AxiosError
 * @param errorLoggerFn A function that will log in the event of a failure to parse the error message.
 * (e.g. console.error or Sentry.captureException)
 */

export const getErrorString = (err: any, errorLoggerFn: Function = Sentry.captureException): string => {
  if (err?.code === 'ECONNABORTED') {
    return 'The connection timed out'
  }

  if (err?.response?.data?.message) {
    return err.response.data.message
  }

  if (err?.response?.data?.error) {
    return err.response.data.error
  }

  if (err?.response?.data?.errors) {
    if (Array.isArray(err.response.data.errors)) {
      return ucFirst(err.response.data.errors.join(', '))
    }

    if (typeof err.response.data.errors === 'string') {
      return ucFirst(err.response.data.errors)
    }

    return ucFirst(
      Object.keys(err.response.data.errors)
        .map(key => {
          let errorMessages = err.response.data.errors[key]

          if (Array.isArray(errorMessages)) {
            errorMessages = errorMessages.join(', ')
          }
          return `${camelToTitle(key).replace(' Id', '')} ${errorMessages}`
        })
        .join(', ')
    )
  }

  if (err?.response?.status === 500) {
    return 'Something went wrong, please try again.'
  }

  // We should report when we don't handle an error properly and add handling for that case
  errorLoggerFn(err)

  return err?.message
}
